import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { getSignedUrl } from "@/lib/storage";
import { useToast } from "@/hooks/use-toast";

type Item = { name: string; path: string; type: "image" | "pdf" | "other" };

export function EvidencePreviewModal({
  isOpen,
  onClose,
  evidenceList,
  startIndex = 0,
  questionTitle,
  title,
}: {
  isOpen: boolean;
  onClose: () => void;
  evidenceList: Item[];
  startIndex?: number;
  questionTitle?: string;
  title?: string;
}) {
  const [signedUrl, setSignedUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const item = evidenceList[startIndex];

  useEffect(() => {
    if (isOpen && item) {
      const loadUrl = async () => {
        setLoading(true);
        try {
          const url = await getSignedUrl(item.path, 60);
          setSignedUrl(url);
        } catch (error: any) {
          toast({
            title: "Error loading file",
            description: error.message || "Failed to load file preview"
          });
        } finally {
          setLoading(false);
        }
      };
      loadUrl();
    }
  }, [isOpen, item, toast]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[80vh]">
        <DialogHeader>
          <DialogTitle className="truncate">
            {title || questionTitle || "Evidence Preview"} — {item?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="w-full h-full rounded-md border bg-background overflow-hidden">
          {!item ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              No preview available.
            </div>
          ) : loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Loading preview...
            </div>
          ) : !signedUrl ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Failed to load file.
            </div>
          ) : item.type === "image" ? (
            <img
              src={signedUrl}
              alt={item.name}
              className="object-contain w-full h-full"
              referrerPolicy="no-referrer"
            />
          ) : item.type === "pdf" ? (
            <iframe
              src={signedUrl}
              title={item.name}
              className="w-full h-full"
              // ACTION 2: no allow-scripts — embedded active content must not execute.
              sandbox="allow-same-origin allow-popups"
              referrerPolicy="no-referrer"
            />

          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground p-6">
              This file type cannot be previewed here. Use the Download button.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}